import { Grid } from '@mui/material'
import { useFormContext } from 'react-hook-form'
import AutocompleteWrapper from './wrappers/AutocompleteWrapper'
import BoxWrapper from './wrappers/BoxWrapper'
import CheckBoxGroupWrapper from './wrappers/CheckBoxGroupWrapper'
import CheckBoxWrapper from './wrappers/CheckBoxWrapper'
import DateFieldWrapper from './wrappers/DateFieldWrapper'
import DividerWrapper from './wrappers/DividerWrapper'
import FieldArrayWrapper from './wrappers/FieldArrayWrapper'
import FileInputWrapper from './wrappers/FileInputWrapper'
import HiddenWrapper from './wrappers/HiddenWrapper'
import InfoText from './wrappers/InfoText'
import LinkWrapper from './wrappers/LinkWrapper'
import RadioGroupWrapper from './wrappers/RadioGroupWrapper'
import SelectWrapper from './wrappers/SelectWrapper'
import TextFieldWrapper from './wrappers/TextFieldWrapper'
import TitleWrapper from './wrappers/TitleWrapper'

const requiredMsg = field => (field.required ? `${field.label}: Campo requerido` : false)

const RULES_BUILDERS = {
  TextField: field => ({ required: requiredMsg(field), min: field.min, max: field.max }),
  CheckBox: field => ({ required: requiredMsg(field) }),
  CheckBoxGroup: field => ({ required: requiredMsg(field) }),
  Select: field => ({ required: requiredMsg(field) }),
  Autocomplete: field => ({ required: requiredMsg(field) }),
  RadioGroup: field => ({
    validate: field.required
      ? value =>
          value === undefined || value === null || value === ''
            ? `${field.label}: Campo requerido`
            : true
      : undefined,
  }),
}

const DISPLAY_ONLY = ['formField', 'index', 'fieldArrayIndex', 'fieldArrayName']
const STD = ['control', 'formField', 'fieldArrayIndex', 'fieldArrayName', 'readOnly']
const STD_IDX = [...STD, 'index']

const WRAPPERS = {
  Title: { C: TitleWrapper, keys: ['formField', 'index'] },
  Divider: { C: DividerWrapper, keys: DISPLAY_ONLY },
  InfoText: { C: InfoText, keys: DISPLAY_ONLY },
  Link: { C: LinkWrapper, keys: DISPLAY_ONLY },
  TextField: { C: TextFieldWrapper, keys: [...STD_IDX, 'rules'] },
  DateField: { C: DateFieldWrapper, keys: STD_IDX },
  CheckBox: { C: CheckBoxWrapper, keys: [...STD_IDX, 'rules'] },
  RadioGroup: { C: RadioGroupWrapper, keys: [...STD, 'rules'] },
  CheckBoxGroup: { C: CheckBoxGroupWrapper, keys: [...STD, 'rules'] },
  Select: { C: SelectWrapper, keys: [...STD, 'rules'] },
  Autocomplete: { C: AutocompleteWrapper, keys: [...STD, 'rules'] },
  FileInput: { C: FileInputWrapper, keys: STD_IDX },
  Hidden: {
    C: HiddenWrapper,
    keys: ['control', 'formField', 'fieldArrayIndex', 'fieldArrayName'],
    useGrid: false,
  },
  FieldArray: {
    C: FieldArrayWrapper,
    keys: ['control', 'fieldArray', 'checkDependencies', 'FieldComponent', 'readOnly'],
    strictDeps: true,
  },
  Box: {
    C: BoxWrapper,
    keys: [...STD, 'fieldArray', 'checkDependencies', 'FieldComponent'],
  },
}

const pickProps = (ctx, keys) =>
  keys.reduce((acc, key) => {
    acc[key] = ctx[key]
    return acc
  }, {})

const FieldGrid = ({ formField, children }) => (
  <Grid
    item
    lg={formField.gridSizing.lg || 6}
    sm={formField.gridSizing.sm || 6}
    xs={formField.gridSizing.xs || 6}
  >
    {children}
  </Grid>
)

const DynamicFieldSwitcher = ({
  formField,
  checkDependencies,
  index,
  readOnly,
  fieldArrayName,
  fieldArrayIndex,
}) => {
  const { control, watch } = useFormContext()

  const entry = WRAPPERS[formField.component_type]
  if (!entry) return null

  const deps = formField.dependencies ?? {}

  const passesDisplay = entry.strictDeps
    ? checkDependencies(deps.display, 'display', index, watch, false)
    : checkDependencies(deps.display, 'display', index, watch)
  if (!passesDisplay) return null

  const getEffectiveReadOnly = () =>
    watch('notApplicable') || readOnly || checkDependencies(deps.disabled, 'disable', index, watch)

  const ctx = {
    control,
    formField,
    fieldArray: formField,
    index,
    fieldArrayName,
    fieldArrayIndex,
    readOnly: entry.keys.includes('readOnly') ? getEffectiveReadOnly() : undefined,
    rules: RULES_BUILDERS[formField.component_type]?.(formField),
    checkDependencies,
    FieldComponent: DynamicFieldSwitcher,
  }

  const Wrapper = entry.C
  const rendered = <Wrapper key={formField.name} {...pickProps(ctx, entry.keys)} />

  if (entry.useGrid === false) return rendered
  return <FieldGrid formField={formField}>{rendered}</FieldGrid>
}

export default DynamicFieldSwitcher
